import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import apiService from '../services/ApiService';
import { useAuth } from '../auth/AuthContext';
import { adminEnvironment } from '../config/environment';
import '../styles/dashboard.scss';

/**
 * Dashboard admin. In alto la fascia petrolio del portale con i quattro
 * numeri chiave (cliccabili: portano alla pagina dove si agisce), poi le cose
 * da fare e i grafici degli eventi.
 *
 * I numeri vengono da /api/admin/dashboard/overview; gli utenti in attesa si
 * contano dalla lista utenti, caricata a parte per non bloccare la pagina.
 */
interface OverviewDto {
    toolsInDebug: number;
    events24h: number;
    openReports: number;
    usersTotal: number;
    eventsByLevel: { level: string; count: number }[];
    topTools: { toolCode: string; toolName: string; count: number }[];
    trendLast7Days: { date: string; count: number }[];
    generatedAt: string;
}

interface UserLite { approvalStatus: string }

// Colori dei grafici: semantici per i livelli, petrolio/teal per le serie.
const LEVEL_COLORS: Record<string, string> = {
    Error: '#c43c3c',
    Warning: '#d99a1e',
    Info: '#12616a',
};
const SERIES_COLOR = '#12616a';
const AXIS_COLOR = '#748096';
const GRID_COLOR = '#edf0f5';

const baseChart: ApexOptions = {
    chart: { toolbar: { show: false }, fontFamily: 'Inter, Segoe UI, sans-serif', foreColor: AXIS_COLOR, animations: { speed: 350 } },
    grid: { borderColor: GRID_COLOR, strokeDashArray: 3 },
    tooltip: { theme: 'light' },
};

const greeting = (): string => {
    const h = new Date().getHours();
    if (h < 12) return 'Buongiorno';
    if (h < 18) return 'Buon pomeriggio';
    return 'Buonasera';
};

const DashboardPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState<OverviewDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pendingUsers, setPendingUsers] = useState<number | null>(null);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        apiService.adminGetUsers<UserLite>()
            .then(users => setPendingUsers(users.filter(u => u.approvalStatus === 'Pending').length))
            .catch(() => setPendingUsers(null));
        try {
            setData(await apiService.get<OverviewDto>('/api/admin/dashboard/overview'));
        } catch (e: any) {
            setError(e?.message ?? 'Errore caricamento dashboard');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void reload(); }, [reload]);

    const errors24h = data?.eventsByLevel.find(x => x.level === 'Error')?.count ?? 0;

    const donutOpts: ApexOptions = useMemo(() => ({
        ...baseChart,
        chart: { ...baseChart.chart, type: 'donut' },
        labels: (data?.eventsByLevel ?? []).map(x => x.level),
        colors: (data?.eventsByLevel ?? []).map(x => LEVEL_COLORS[x.level] ?? '#94a3b8'),
        legend: { position: 'bottom', fontSize: '12px', markers: { size: 5 } },
        dataLabels: { enabled: false },
        stroke: { width: 3, colors: ['#fff'] },
        plotOptions: {
            pie: {
                donut: {
                    size: '72%',
                    labels: {
                        show: true,
                        name: { fontSize: '12px', color: AXIS_COLOR },
                        value: { fontSize: '26px', fontWeight: 650, color: '#17223b' },
                        total: { show: true, label: 'Eventi', color: AXIS_COLOR, fontSize: '12px' },
                    },
                },
            },
        },
        noData: { text: 'Nessun evento nelle ultime 24h', style: { color: AXIS_COLOR } },
    }), [data]);
    const donutSeries = useMemo(() => (data?.eventsByLevel ?? []).map(x => x.count), [data]);

    const barOpts: ApexOptions = useMemo(() => ({
        ...baseChart,
        chart: {
            ...baseChart.chart,
            type: 'bar',
            events: {
                dataPointSelection: (_e: unknown, _c: unknown, opts: { dataPointIndex: number }) => {
                    const item = data?.topTools[opts.dataPointIndex];
                    if (item) navigate(`/quality/events?toolCode=${encodeURIComponent(item.toolCode)}`);
                },
            },
        },
        plotOptions: { bar: { horizontal: true, borderRadius: 5, borderRadiusApplication: 'end', barHeight: '58%' } },
        dataLabels: { enabled: true, style: { fontSize: '11px', fontWeight: 600, colors: ['#fff'] }, offsetX: -4, textAnchor: 'end' },
        xaxis: { categories: (data?.topTools ?? []).map(x => x.toolName || x.toolCode), axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { maxWidth: 220, style: { fontSize: '12px', colors: '#27354b' } } },
        colors: [SERIES_COLOR],
        noData: { text: 'Nessun evento nelle ultime 24h', style: { color: AXIS_COLOR } },
        tooltip: { ...baseChart.tooltip, y: { formatter: (v: number) => `${v} eventi` } },
    }), [data, navigate]);
    const barSeries = useMemo(() => [{ name: 'Eventi', data: (data?.topTools ?? []).map(x => x.count) }], [data]);

    const lineOpts: ApexOptions = useMemo(() => ({
        ...baseChart,
        chart: { ...baseChart.chart, type: 'area' },
        stroke: { curve: 'smooth', width: 2.5 },
        colors: [SERIES_COLOR],
        fill: { type: 'gradient', gradient: { shadeIntensity: 0, opacityFrom: 0.28, opacityTo: 0.02, stops: [0, 95] } },
        markers: { size: 0, hover: { size: 5 } },
        dataLabels: { enabled: false },
        xaxis: {
            type: 'datetime',
            categories: (data?.trendLast7Days ?? []).map(x => x.date),
            labels: { datetimeFormatter: { day: 'dd MMM' } },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: { min: 0, forceNiceScale: true, labels: { formatter: (v: number) => `${Math.round(v)}` } },
        noData: { text: 'Nessun dato disponibile', style: { color: AXIS_COLOR } },
    }), [data]);
    const lineSeries = useMemo(() => [{ name: 'Eventi', data: (data?.trendLast7Days ?? []).map(x => x.count) }], [data]);

    const weekTotal = (data?.trendLast7Days ?? []).reduce((s, x) => s + x.count, 0);

    const todo = [
        {
            key: 'users', icon: 'bi-person-check', to: '/users',
            count: pendingUsers, label: 'Utenti in attesa di approvazione',
            hint: 'Approva e assegna i tool, oppure rifiuta con un motivo.',
        },
        {
            key: 'reports', icon: 'bi-flag', to: '/quality/reports',
            count: data?.openReports ?? null, label: 'Segnalazioni aperte',
            hint: 'Segnalazioni inviate dagli utenti dai tool.',
        },
        {
            key: 'errors', icon: 'bi-bug', to: '/quality/events?level=Error',
            count: data ? errors24h : null, label: 'Errori nelle ultime 24 ore',
            hint: 'Eventi di livello Error raccolti dai tool.',
        },
        {
            key: 'debug', icon: 'bi-cone-striped', to: '/tools',
            count: data?.toolsInDebug ?? null, label: 'Tool in modalità Debug',
            hint: 'Visibili solo a chi li sta verificando.',
        },
    ];
    const openTodo = todo.filter(t => (t.count ?? 0) > 0);

    const name = user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || '';

    return (
        <div className="admin-dashboard">
            <section className="dash-hero">
                <div className="dash-hero__copy">
                    <div className="dash-hero__top">
                        <span className="dash-hero__kicker">Panoramica · {adminEnvironment.label}</span>
                        <button type="button" className="btn btn-ghost-light btn-sm" onClick={reload} disabled={loading}>
                            <i className={`bi bi-arrow-clockwise me-1 ${loading ? 'spin' : ''}`} />
                            {loading ? 'Aggiorno…' : 'Aggiorna'}
                        </button>
                    </div>
                    <h1>{greeting()}{name ? `, ${name}` : ''}.</h1>
                    <p>
                        {loading && !data
                            ? 'Sto raccogliendo lo stato della piattaforma…'
                            : openTodo.length === 0
                                ? 'Niente in sospeso: nessuna approvazione, segnalazione o errore da gestire.'
                                : `Ci sono ${openTodo.length} ${openTodo.length === 1 ? 'area che richiede' : 'aree che richiedono'} attenzione.`}
                    </p>
                    {data?.generatedAt && (
                        <span className="dash-hero__time">
                            Aggiornato alle {new Date(data.generatedAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    )}
                </div>

                <div className="dash-hero__metrics">
                    <Metric to="/users" label="Utenti registrati" value={data?.usersTotal} loading={loading} icon="bi-people" />
                    <Metric to="/quality/events" label="Eventi 24h" value={data?.events24h} loading={loading} icon="bi-activity" />
                    <Metric to="/quality/reports" label="Segnalazioni aperte" value={data?.openReports} loading={loading} icon="bi-flag" tone={data && data.openReports > 0 ? 'warn' : undefined} />
                    <Metric to="/tools" label="Tool in debug" value={data?.toolsInDebug} loading={loading} icon="bi-cone-striped" />
                </div>
            </section>

            {error && (
                <div className="alert alert-danger d-flex align-items-center mt-3" role="alert">
                    <i className="bi bi-exclamation-triangle me-2" />
                    <span className="flex-grow-1">{error}</span>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={reload}>Riprova</button>
                </div>
            )}

            <div className="dash-grid">
                <section className="dash-panel dash-todo">
                    <header className="dash-panel__head">
                        <div>
                            <span className="dash-panel__kicker">Da fare</span>
                            <h2>Cosa richiede attenzione</h2>
                        </div>
                    </header>
                    <ul className="dash-todo__list">
                        {todo.map(t => {
                            const active = (t.count ?? 0) > 0;
                            return (
                                <li key={t.key}>
                                    <Link to={t.to} className={`dash-todo__item ${active ? 'is-active' : ''}`}>
                                        <span className="dash-todo__icon"><i className={`bi ${t.icon}`} /></span>
                                        <span className="dash-todo__text">
                                            <strong>{t.label}</strong>
                                            <small>{t.hint}</small>
                                        </span>
                                        <span className="dash-todo__count">
                                            {t.count === null ? (loading ? '…' : '—') : t.count}
                                        </span>
                                        <i className="bi bi-chevron-right dash-todo__arrow" aria-hidden="true" />
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </section>

                <section className="dash-panel">
                    <header className="dash-panel__head">
                        <div>
                            <span className="dash-panel__kicker">Ultime 24 ore</span>
                            <h2>Eventi per livello</h2>
                        </div>
                        <Link to="/quality/events" className="dash-link">Eventi <i className="bi bi-arrow-right" /></Link>
                    </header>
                    <Chart options={donutOpts} series={donutSeries} type="donut" height={270} />
                </section>

                <section className="dash-panel dash-panel--wide">
                    <header className="dash-panel__head">
                        <div>
                            <span className="dash-panel__kicker">Ultimi 7 giorni</span>
                            <h2>Andamento eventi</h2>
                        </div>
                        <div className="dash-panel__stat">
                            <strong>{loading && !data ? '…' : weekTotal.toLocaleString('it-IT')}</strong>
                            <span>eventi totali</span>
                        </div>
                    </header>
                    <Chart options={lineOpts} series={lineSeries} type="area" height={260} />
                </section>

                <section className="dash-panel">
                    <header className="dash-panel__head">
                        <div>
                            <span className="dash-panel__kicker">Ultime 24 ore</span>
                            <h2>Tool con più eventi</h2>
                        </div>
                        <Link to="/quality" className="dash-link">Quality Center <i className="bi bi-arrow-right" /></Link>
                    </header>
                    <Chart options={barOpts} series={barSeries} type="bar" height={260} />
                </section>
            </div>
        </div>
    );
};

const Metric: React.FC<{
    to: string; label: string; value: number | undefined; loading: boolean; icon: string; tone?: 'warn';
}> = ({ to, label, value, loading, icon, tone }) => (
    <Link to={to} className={`dash-metric ${tone === 'warn' ? 'is-warn' : ''}`}>
        <i className={`bi ${icon}`} aria-hidden="true" />
        <span className="dash-metric__value">
            {loading && value === undefined ? <span className="dash-metric__skeleton" /> : (value?.toLocaleString('it-IT') ?? '—')}
        </span>
        <span className="dash-metric__label">{label}</span>
    </Link>
);

export default DashboardPage;
